import { useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { X, Lock, Check, Zap } from 'lucide-react'
import { Button } from '@brix/ui'

const stripePromise = loadStripe(import.meta.env['VITE_STRIPE_PUBLISHABLE_KEY'] as string)

interface PlanSummary {
  name: string
  price: string
  interval: string
  currency: string
}

interface PaymentFormProps {
  plan: PlanSummary
  onSuccess: () => void
}

function PaymentForm({ plan, onSuccess }: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setProcessing(true)
    setError(null)

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Stripe may redirect for 3DS — send back to billing on success
        return_url: `${window.location.origin}/billing?success=1`,
      },
      // For cards that don't need redirect, handle inline
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(stripeError.message ?? 'Payment failed. Please try again.')
      setProcessing(false)
      return
    }

    // Payment confirmed without redirect (no 3DS required)
    setProcessing(false)
    onSuccess()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Plan summary */}
      <div className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-violet-500 font-medium uppercase tracking-wide">Plan {plan.name}</p>
            <p className="text-sm text-violet-900 mt-0.5">
              {plan.interval === 'month' ? 'Monthly' : 'Annual'} billing
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-violet-900">{plan.price}</p>
            <p className="text-xs text-violet-500">
              {plan.currency.toUpperCase()} / {plan.interval === 'month' ? 'mo' : 'yr'}
            </p>
          </div>
        </div>
      </div>

      {/* Stripe Payment Element */}
      <div>
        <PaymentElement
          options={{
            layout: 'tabs',
            fields: { billingDetails: { name: 'auto' } },
          }}
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={!stripe || processing}
        loading={processing}
        className="w-full flex items-center justify-center gap-2 !py-3"
      >
        <Zap className="h-4 w-4" />
        {processing ? 'Processing…' : `Subscribe to ${plan.name}`}
      </Button>

      <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
        <Lock className="h-3 w-3" />
        Secured by Stripe · Cancel anytime
      </div>
    </form>
  )
}

interface PaymentModalProps {
  clientSecret: string
  plan: PlanSummary
  onSuccess: () => void
  onClose: () => void
}

export function PaymentModal({ clientSecret, plan, onSuccess, onClose }: PaymentModalProps) {
  const [paid, setPaid] = useState(false)

  const handleSuccess = () => {
    setPaid(true)
    setTimeout(onSuccess, 1800)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Complete your subscription</h2>
            <p className="text-sm text-gray-500 mt-0.5">Brix · {plan.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
          {paid ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
              <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-7 w-7 text-green-600" />
              </div>
              <p className="text-lg font-semibold text-gray-900">Subscription activated!</p>
              <p className="text-sm text-gray-500">Welcome to the {plan.name} plan.</p>
            </div>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#7c3aed',
                    colorBackground: '#ffffff',
                    colorText: '#111827',
                    colorDanger: '#dc2626',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    borderRadius: '8px',
                    spacingUnit: '4px',
                  },
                },
              }}
            >
              <PaymentForm plan={plan} onSuccess={handleSuccess} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  )
}
